import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'

import { PublicClassNames } from 'embed/constants'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { Assistant } from 'embed/components/Application/Assistant'
import { BackButton } from 'embed/components/Application/BackButton'
import { NoAffect } from 'embed/components/Application/NoAffect'
import { ProgressBar } from 'embed/components/Application/ProgressBar'
import { ReturnToSearch } from 'embed/components/Application/ReturnToSearch'
import { TCPALanguage } from 'embed/components/Application/TCPALanguage'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton'
import ApplicationSubmitButtonContainer from 'embed/components/ApplicationSubmitButton/container'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { CommonState } from 'embed/reducers'
import { loanHasCoApplicantFieldSelector } from 'embed/selectors/application'
import { configShouldAllowCoApplicantSelector, isFionaAccountCreationEnabled } from 'embed/selectors/config'
import { Experiment, Original, StepByStepAlternateContinueButtonLang, Variant, withExperiment } from 'embed/experiments'
import { LoanStep, Product } from 'even-ts-static'
import Header from '../../Header'

const applicationStyles = require('embed/styles/application')
const styles = require('./styles')

interface OwnProps {
  backToSearch?: boolean
  children: React.ReactNode
  directReferral?: boolean
  disableCTA?: boolean
  dispatchBackToStepOne: () => void
  getOffersCta?: boolean
  handleConsentsToFCRAandTCPA?: () => void
  onBack: () => void
  onContinue: () => void
  stepByStepApplicationStepIsValid: boolean
  currentStep: number
  totalNumberOfSteps: number
  selectedVariantId?: string
}

interface StoreProps {
  hasCoApplicantIsDefined: boolean
  shouldAllowCoApplicant: boolean
  enableAccountCreation: boolean
}

type Props = OwnProps & StoreProps

const BUTTON_TEXT = 'Agree and Continue'

const StepbystepWrapper: React.SFC<Props> = (props) => {
  let headerLink: React.ReactNode
  if (props.backToSearch) {
    headerLink = !props.directReferral ? (
      <ReturnToSearch className={applicationStyles.backToSearch} onClick={props.dispatchBackToStepOne} />
    ) : null
  } else {
    headerLink = <BackButton className={applicationStyles.backToSearch} onClick={props.onBack} />
  }

  let ctaButton: React.ReactNode
  const isLastStep = props.currentStep + 1 === props.totalNumberOfSteps
  const hasCoApplicantIsDefinedIfAllowed = !props.shouldAllowCoApplicant || props.hasCoApplicantIsDefined
  if (props.disableCTA === true) {
    ctaButton = null
  } else if (isLastStep && hasCoApplicantIsDefinedIfAllowed) {
    ctaButton = (
      <>
        <ApplicationSubmitButtonContainer
          product={Product.Loan}
          handleBeforeSubmit={props.handleConsentsToFCRAandTCPA}
          nextStep={props.enableAccountCreation ? LoanStep.CreateAccount : LoanStep.Results}
        >
          <span className={applicationStyles.consentButtonLabel}>{BUTTON_TEXT}</span>
        </ApplicationSubmitButtonContainer>
        <TCPALanguage buttonText={BUTTON_TEXT} className={applicationStyles.consentWithButton} product={Product.Loan} />
      </>
    )
  } else if (props.getOffersCta) {
    ctaButton = <ApplicationSubmitButtonContainer fullWidth={true} nextStep={LoanStep.Results} product={Product.Loan} />
  } else {
    ctaButton = (
      <Experiment experiment={StepByStepAlternateContinueButtonLang}>
        <Original>
          <ApplicationSubmitButton
            fullWidth={true}
            isValid={props.stepByStepApplicationStepIsValid}
            onSubmit={props.onContinue}
          >
            Continue
          </ApplicationSubmitButton>
        </Original>
        <Variant
          experiment={StepByStepAlternateContinueButtonLang}
          variant={StepByStepAlternateContinueButtonLang.variants.GetStarted}
        >
          <ApplicationSubmitButton
            fullWidth={true}
            isValid={props.stepByStepApplicationStepIsValid}
            onSubmit={props.onContinue}
          >
            {props.currentStep === 1 ? 'Get Started' : 'Continue'}
          </ApplicationSubmitButton>
        </Variant>
      </Experiment>
    )
  }

  return (
    <ThemeConsumer>
      {({ backdropStyles, borderRadiusLarge, boxStyles, colorBorder }) => (
        <React.Fragment>
          <Header />
          <div
            className={classnames(
              PublicClassNames.ApplicationContainer,
              applicationStyles.container,
              styles.application,
              css`
                background-color: ${backdropStyles.backgroundColor};
                border-color: ${colorBorder};
                border-radius: ${borderRadiusLarge};
              `,
            )}
            style={boxStyles}
          >
            <div className={classnames(applicationStyles.applicationHeader, styles.applicationHeader)}>
              {headerLink}
              <div className={applicationStyles.advertiserDisclosure}>
                <AdvertiserDisclosure />
              </div>
            </div>
            <ProgressBar
              totalNumberOfSteps={props.totalNumberOfSteps}
              currentStep={props.currentStep + 1}
              className={styles.progressBar}
            />
            <section className={applicationStyles.formSection}>
              <div className={applicationStyles.centeredField}>
                <Assistant />
                {props.children}
              </div>
            </section>
            {ctaButton}
            <NoAffect />
          </div>
        </React.Fragment>
      )}
    </ThemeConsumer>
  )
}

const mapStateToProps = (state: CommonState): StoreProps => ({
  hasCoApplicantIsDefined: loanHasCoApplicantFieldSelector(state)?.value !== undefined,
  shouldAllowCoApplicant: configShouldAllowCoApplicantSelector(state),
  enableAccountCreation: isFionaAccountCreationEnabled(state),
})

export default withExperiment(StepByStepAlternateContinueButtonLang)(connect(mapStateToProps)(StepbystepWrapper))
